import { registerSW } from "virtual:pwa-register";

import { HOME_PAGE_VIDEO_URLS } from "./App.constants";
import {
  getNavigatorNetworkConnection,
  prefetchHomeVideosInBatches,
  shouldSkipHomeVideoCacheWarm,
} from "./services/pwa/home-video-cache-warm/home-video-cache-warm";
import { deleteObsoleteRuntimeCaches } from "./services/pwa/obsolete-cache-cleanup/obsolete-cache-cleanup";
import { captureException } from "./utils/sentry";

// --- Module-level refs for cross-module communication ---

let updateSWRef: ((reloadPage?: boolean) => Promise<void>) | undefined;
let registrationRef: ServiceWorkerRegistration | undefined;

// Buffered pub/sub for update-available events.
// Listeners registered after the event already fired still get notified.
const needRefreshListeners = new Set<() => void>();
let needRefreshFired = false;

/**
 * Subscribe to the "update available" event from vite-plugin-pwa.
 * If the event already fired before subscription, the callback runs immediately.
 */
export function onNeedRefresh(callback: () => void): () => void {
  needRefreshListeners.add(callback);
  if (needRefreshFired) {
    callback();
  }
  return () => {
    needRefreshListeners.delete(callback);
  };
}

/**
 * Returns the updateSW function from vite-plugin-pwa.
 * Call with `true` to send SKIP_WAITING and reload.
 */
export function getUpdateSW():
  | ((reloadPage?: boolean) => Promise<void>)
  | undefined {
  return updateSWRef;
}

/**
 * Returns the current SW registration for manual update checks
 * via `registration.update()`.
 */
export function getRegistration(): ServiceWorkerRegistration | undefined {
  return registrationRef;
}

// Keeps background work from competing with critical startup work.
function runWhenIdle(task: () => void) {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(() => task());
  } else {
    setTimeout(task, 2000);
  }
}

// Prefetch videos into the SW runtime cache so they are available offline.
// Must run only when the SW is the active controller — otherwise fetches
// bypass the SW and nothing gets cached.
// Batched prefetch avoids six parallel full downloads on cold start.
// mode: "cors" matches how <video crossOrigin="anonymous"> requests them,
// ensuring the cached response is a proper CORS 200 (not an opaque status-0).
function warmVideoCache() {
  if (shouldSkipHomeVideoCacheWarm(getNavigatorNetworkConnection())) {
    return;
  }

  runWhenIdle(() => void prefetchHomeVideosInBatches(HOME_PAGE_VIDEO_URLS));
}

// Reclaims quota on devices that installed an earlier service worker whose
// runtime routes no longer exist. Failures are non-fatal: the caches are dead
// weight, not something the app reads from.
function cleanupObsoleteCaches() {
  runWhenIdle(() => {
    void deleteObsoleteRuntimeCaches(
      "caches" in window ? window.caches : undefined
    )
      .then((deleted) => {
        if (deleted.length > 0) {
          console.log(
            `🧹 Removed ${deleted.length} obsolete runtime cache(s):`,
            deleted.join(", ")
          );
        }
      })
      .catch(() => {});
  });
}

export function initServiceWorker(): void {
  updateSWRef = registerSW({
    immediate: true,
    onNeedRefresh() {
      console.log("📦 New version available and waiting");
      needRefreshFired = true;
      for (const listener of needRefreshListeners) {
        listener();
      }
    },
    onOfflineReady() {
      console.log("✅ App ready for offline use");
    },
    onRegisteredSW(
      swScriptUrl: string,
      registration: ServiceWorkerRegistration | undefined
    ) {
      console.log("✅ Service Worker registered:", swScriptUrl);
      registrationRef = registration ?? undefined;

      if (!registration) {
        return;
      }

      cleanupObsoleteCaches();

      // Warm the video cache once the SW is controlling this page.
      if (navigator.serviceWorker.controller) {
        warmVideoCache();
      } else {
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          () => warmVideoCache(),
          { once: true }
        );
      }
    },
    onRegisterError(error: Error) {
      console.error("❌ Service Worker registration error:", error);
      captureException(error, { type: "sw-registration-failure" });
    },
  });
}
